import { useEffect, useState } from "react";
import { Box, Button, Typography, Paper, Grid, TextField, ToggleButtonGroup, ToggleButton, Autocomplete, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormHelperText, Divider, LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { DatePicker } from "@mui/x-date-pickers";
import * as yup from 'yup'
import dayjs from "dayjs";
import ThankYou from "./ThankYouPage";
import SiblingDialog from "./SiblingDialog";
import ELLDialog from "./ELLDialog";
import FinancialDialog from "./FinancialDialog";
import RequiredLabel from "components/RequiredLabel";
import PhoneField from "components/PhoneField";
import { PHONE_REGEXP } from "utils/phone";
import GoogleSearchBox from "components/AddressAutocomplete";
import CutoffDialog from "./CutoffDialog";
import { CheckBoxOutlineBlank, CheckBox, IndeterminateCheckBox } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { saveApplication } from "store/application/application";
import { getIneligibleDistricts } from "./utils/cutoff";
import * as snackbar from "utils/snackbar";
import { SUFFIX_OPTIONS as suffixOptions, PHONE_TYPE_OPTIONS as phoneTypes } from "constants";
import { search } from "store/application/application-setting";
import SpecialEdDialog from "./SpecialEdDialog";
import StaffPriorityDialog from "./StaffPriorityDialog";


const CYCLE_OPENS_MONTH = 8;

const getApplicationYear = (today = new Date()) =>
    today.getFullYear() + (today.getMonth() >= CYCLE_OPENS_MONTH ? 1 : 0);

const onlyLatin = /^[A-Za-z\s'-]+$/;

export default function ApplicationForm() {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const [openSiblingInfo, setOpenSiblingInfo] = useState(false);
    const [openELLInfo, setOpenELLInfo] = useState(false);
    const [openFinancialInfo, setOpenFinancialInfo] = useState(false);
    const [cutoffDialogOpen, setCutoffDialogOpen] = useState(false);
    const [cutoffMessage, setCutoffMessage] = useState("");
    const [submissionData, setSubmissionData] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [openSpecialEdInfo, setOpenSpecialEdInfo] = useState(false);
    const [openStaffInfo, setOpenStaffInfo] = useState(false);
    const districtOptions = useSelector(state => state.application.applicationSetting.list)

    const currentYear = getApplicationYear();
    const cutoff = new Date(currentYear, 11, 31);

    const maxDob = new Date(cutoff);
    maxDob?.setFullYear(maxDob?.getFullYear() - 19);

    const handleLangChange = (event, lang) => {
        i18n.changeLanguage(lang)
    }

    const languages = {
        en: { nativeName: 'English' },
        es: { nativeName: 'Spanish' },
    }

    const renderHeader = () => (
        <>
            <Typography variant="h4" textAlign={'center'} mb={2}>
                {t("Enrollment Application")}
            </Typography>
            <Typography variant="body1" textAlign={'center'} fontWeight={500}>
                {t("Incorrect, or duplicate applications will not be accepted. All details will be checked against official documents (e.g., birth certificate). If you are not sure, we can help.")}
            </Typography>
        </>
    );

    const renderiLearnLogo = () => (
        <Box sx={{ display: "flex", justifyContent: "center", alignSelf: 'center', mb: 3 }}>
            <img
                src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/08/28112613/iLearn-Logo-White.png"
                alt="iLearn Logo"
                style={{ width: "250px" }}
            />
        </Box>
    );

    const renderSchoolLogos = () => (
        <Paper sx={{ mt: 3, gap: 1, display: "flex", justifyContent: "center", alignSelf: 'center', p: 1, borderRadius: 2, flexWrap: 'wrap' }} elevation={3}>
            <img
                src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/08/28135110/iLearn-Icon-Bergen-Alternate.jpg"
                alt="Bergen Icon"
                style={{ width: "60px" }}
            />
            <img
                src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/08/28135129/iLearn-Icon-Passaic-Alternate.jpg"
                alt="Passaic Icon"
                style={{ width: "60px" }}
            />
            <img
                src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/08/28135239/iLearn-Icon-Paterson-Alternate.jpg"
                alt="Paterson Icon"
                style={{ width: "60px" }}
            />
            <img
                src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/08/28135157/iLearn-Icon-Hudson-Alternate.png"
                alt="Hudson Icon"
                style={{ width: "60px" }}
            />
            <img
                src="https://ils.sfo2.digitaloceanspaces.com/var/www/html/2025/09/11112834/iLearn-Icon-Bronx-Alternate.png"
                alt="Bronx Icon"
                style={{ width: "60px" }}
            />
        </Paper>
    );

    const schema = yup
        .object()
        .shape({
            firstName: yup.string().trim().required(t("First name is required")).matches(onlyLatin, t("Only English letters are allowed")),
            lastName: yup.string().trim().required(t("Last name is required")).matches(onlyLatin, t("Only English letters are allowed")),
            dateOfBirth: yup
                .date()
                .required(t("Date of birth is required")),
            grade: yup.string().trim().required(t("Grade is required")),
            address: yup.mixed().required(t("Address is required")),
            districtApplying: yup
                .array()
                .min(1, t("Select at least one district"))
                .required(t("District is required"))
                .test("dob-cutoff", function (districts) {
                    const { dateOfBirth, grade } = this.parent;
                    const { createError, path } = this;

                    const invalidDistricts = getIneligibleDistricts(dateOfBirth, districts, districtOptions, grade, currentYear);
                    if (invalidDistricts.length > 0) {
                        const names = invalidDistricts.map(d => d.name).join(", ");
                        return createError({ path, message: `Your child does not meet the age cutoff for: ${names}.` });
                    }
                    return true;
                }),
            hasSibling: yup.string().trim().required(t("Please select Yes or No")),
            siblingFirstName: yup
                .string().when("hasSibling", {
                    is: "yes",
                    then: schema => schema.required(t("Sibling first name is required")).matches(onlyLatin, t("Only English letters are allowed")),
                    otherwise: schema => schema.notRequired()
                }),
            siblingLastName: yup
                .string().trim().when("hasSibling", {
                    is: "yes",
                    then: schema => schema.required(t("Sibling last name is required")).matches(onlyLatin, t("Only English letters are allowed")),
                    otherwise: schema => schema.notRequired()
                }),
            siblingGrade: yup.string().trim().when("hasSibling", {
                is: "yes",
                then: schema => schema.required(t("Sibling grade is required")),
                otherwise: schema => schema.notRequired()
            }),
            currentSchool: yup.string().trim().required(t("Current school is required")),
            parentFirstName: yup.string().trim().required(t("Parent/Guardian first name is required")).matches(onlyLatin, t("Only English letters are allowed")),
            parentLastName: yup.string().trim().required(t("Parent/Guardian last name is required")).matches(onlyLatin, t("Only English letters are allowed")),
            phoneType: yup
                .string()
                .required(t("Phone Type is required")),
            phoneNumber: yup
                .string()
                .required(t("Phone Number is required"))
                .matches(PHONE_REGEXP, t("Enter a valid phone number")),
            email: yup
                .string()
                .required(t("Email is required"))
                .email(t("Enter a valid email")),
            hasEmployedParent: yup.string().trim().required(t("Please select Yes or No")),
            staffEmail: yup
                .string()
                .when("hasEmployedParent", {
                    is: "yes",
                    then: schema => schema
                        .required(t("Work email is required"))
                        .email(t("Enter a valid email")),
                    otherwise: schema => schema.notRequired()
                }),
            isELL: yup.string().when("districtApplying", {
                is: (districtIds) => {
                    if (!Array.isArray(districtIds) || districtIds.length === 0) return false;
                    const selected = districtOptions?.filter(d => districtIds.includes(d.id));
                    return selected?.some(d => d.options?.isELL);
                },
                then: schema => schema.required(t("Please select Yes or No")),
                otherwise: schema => schema.notRequired()
            }),
            isLowIncome: yup.string().when("districtApplying", {
                is: (districtIds) => {
                    if (!Array.isArray(districtIds) || districtIds.length === 0) return false;
                    const selected = districtOptions?.filter(d => districtIds.includes(d.id));
                    return selected?.some(d => d.options?.isLowIncome);
                },
                then: schema => schema.required(t("Please select Yes or No")),
                otherwise: schema => schema.notRequired()
            }),
            isSWD: yup.string().when("districtApplying", {
                is: (districtIds) => {
                    if (!Array.isArray(districtIds) || districtIds.length === 0) return false;
                    const selected = districtOptions?.filter(d => districtIds.includes(d.id));
                    return selected?.some(d => d.options?.isSWD);
                },
                then: schema => schema.required(t("Please select Yes or No")),
                otherwise: schema => schema.notRequired()
            }),
            certification: yup
                .boolean()
                .oneOf([true], t("You must confirm the certification"))
        })

    const { control, handleSubmit, watch, resetField, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            firstName: '',
            middleName: '',
            lastName: '',
            suffix: '',
            grade: '',
            districtApplying: [],
            hasSibling: '',
            siblingFirstName: '',
            siblingLastName: '',
            siblingGrade: '',
            currentSchool: '',
            parentFirstName: '',
            parentLastName: '',
            address: '',
            phoneType: 'mobile',
            phoneNumber: '',
            email: '',
            certification: false,
            isELL: '',
            isLowIncome: '',
            isSWD: '',
            hasEmployedParent: '',
            staffEmail: '',
        },
    })

    const selectedDistricts = watch("districtApplying");
    const selectedGrade = watch("grade");
    const selectedState = watch("addressState");
    const hasSibling = watch("hasSibling");
    const isELL = watch("isELL");
    const isLowIncome = watch("isLowIncome");
    const isSWD = watch("isSWD");
    const hasEmployedParent = watch("hasEmployedParent");

    const selectedDistrictConfigs = districtOptions?.filter(d =>
        selectedDistricts?.includes(d.id)
    );

    const hasELL = selectedDistrictConfigs?.some(d => d.options?.isELL);
    const hasLowIncome = selectedDistrictConfigs?.some(d => d.options?.isLowIncome);
    const hasSwd = selectedDistrictConfigs?.some(d => d.options?.isSWD);
    const showWeightedLottery = hasELL || hasLowIncome || hasSwd;

    const siblingQuestion = selectedState === 'NY'
        ? t('Does your child have a brother or sister currently enrolled or applying at the same time to Bronx Arts & Science Charter School?')
        : t('Does your child have a brother or sister already attending this school?')

    const gradeOptions = (selectedState === "NY"
        ? [{ label: "Pre-K", value: "Pre-K" }]
        : []
    ).concat([
        { label: "Kindergarten", value: "K" },
        ...Array.from({ length: 12 }, (_, i) => ({
            label: `Grade ${i + 1}`,
            value: `${i + 1}`
        }))
    ]);

    const getMinDob = (grade, state) => {
        if (state === "NJ") {
            return new Date(currentYear - 5, 9, 31);
        }

        if (state === "NY" && grade === "Pre-K") {
            return new Date(currentYear - 4, 11, 31);
        }

        if (state === "NY" && grade === "K") {
            return new Date(currentYear - 5, 11, 31);
        }

        const fallback = new Date(currentYear - 5, 12, 31);
        return fallback;
    };

    const minDob = getMinDob(selectedGrade, selectedState)

    const onSubmit = async data => {

        const capitalizeWords = str =>
            typeof str === "string"
                ? str
                    .toLowerCase()
                    .replace(/(^\w|[\s-]\w)/g, match => match.toUpperCase())
                : str;

        const payload = {
            student: {
                yearApp: 27,
                gradeApp: data.grade,
                lastName: capitalizeWords(data.lastName),
                firstName: capitalizeWords(data.firstName),
                middleName: capitalizeWords(data.middleName) || "",
                suffix: data.suffix || "",
                dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format("YYYY-MM-DD") : null,
                currentSchool: data.currentSchool,
                parentLastName: capitalizeWords(data.parentLastName),
                parentFirstName: capitalizeWords(data.parentFirstName),
                parentPhone: data.phoneNumber,
                type: data.phoneType,
                parentEmail: data.email.toLowerCase(),
                ellStudent: data.isELL === "yes",
                edcStudent: data.isLowIncome === "yes",
                swdStudent: data.isSWD === "yes",
                sibling: data.hasSibling === "yes",
                staffPriority: data.hasEmployedParent === "yes",
                ...(data.staffEmail ? { staffEmail: data.staffEmail.toLowerCase() } : {}),
                ...(data.siblingFirstName ? { siblingFirstName: capitalizeWords(data.siblingFirstName) } : {}),
                ...(data.siblingLastName ? { siblingLastName: capitalizeWords(data.siblingLastName) } : {}),
                ...(data.siblingGrade ? { siblingGrade: data.siblingGrade } : {}),
            },
            address: {
                ...data.address,
                line2: capitalizeWords(data.line2) || "",
                type: "home"
            },
            schools: data.districtApplying,
        }
        const res = await dispatch(saveApplication(payload))
        if (res.payload) {
            setSubmissionData(res);
            setSubmitted(true);
        }

        if (res.error?.data?.error === "year_app must be unique") {
            snackbar.error(t("You have already submitted an application for this student. If you need to make changes, please contact your school campus directly."));
        } else if (res.error) {
            snackbar.error(t("There was an error submitting your application. Please try again later."));
        }

    }

    useEffect(() => {
        if (!submitted && hasSibling === "yes") {
            setOpenSiblingInfo(true);
        }
    }, [hasSibling, submitted]);

    useEffect(() => {
        if (!submitted && isELL === "yes") {
            setOpenELLInfo(true);
        }
    }, [isELL, submitted]);

    useEffect(() => {
        if (!submitted && isLowIncome === "yes") {
            setOpenFinancialInfo(true);
        }
    }, [isLowIncome, submitted]);

    useEffect(() => {
        if (!submitted && isSWD === "yes") {
            setOpenSpecialEdInfo(true);
        }
    }, [isSWD, submitted]);

    useEffect(() => {
        if (hasSibling === "no") {
            resetField("siblingFirstName");
            resetField("siblingLastName");
            resetField("siblingGrade");
        }
    }, [hasSibling, resetField]);

    useEffect(() => {
        if (!submitted && hasEmployedParent === "yes") {
            setOpenStaffInfo(true);
        }
    }, [hasEmployedParent, submitted]);

    useEffect(() => {
        if (hasEmployedParent === "no") {
            resetField("staffEmail");
        }
    }, [hasEmployedParent, resetField]);

    useEffect(() => {
        resetField("districtApplying", { defaultValue: [] });
    }, [selectedState, resetField]);


    useEffect(() => {
        districtOptions || dispatch(search());
    }, [districtOptions, dispatch]);

    if (!districtOptions) return <LinearProgress />

    return (
        <>
            {!submitted ? (

                <Box sx={{ display: "flex" }}>
                    <Box
                        sx={{
                            width: { md: "500px" },
                            backgroundColor: 'primary.main',
                            color: "#fff",
                            padding: 8,
                            flexDirection: "column",
                            justifyContent: "center",
                            borderTopRightRadius: 300,
                            borderBottomRightRadius: 300,
                            zIndex: 2,
                            display: { xs: "none", sm: "none", md: "flex" },
                            height: "100vh"
                        }}
                    >
                        {renderiLearnLogo()}
                        {renderHeader()}
                        {renderSchoolLogos()}
                    </Box>

                    <Paper
                        elevation={3}
                        sx={{
                            position: { md: "absolute" },
                            left: { md: 280 },
                            top: {
                                xs: "auto",
                                md: "50%",
                            },
                            transform: {
                                xs: "none",
                                md: "translateY(-50%)",
                            },
                            width: { xs: "100%", sm: "100%", md: "80%" },
                            height: { xs: "100%", sm: "100%", md: "90vh" },
                            minHeight: { xs: "100%", md: "80vh" },
                            padding: { xs: 1, md: 4 },
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            overflow: "auto",
                            '@media (min-width: 900px)': {
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }
                        }}
                    >
                        <Box ml={{ xs: 0, sm: 0, md: 30 }} component="form" onSubmit={handleSubmit(onSubmit)}>
                            <Box sx={{ display: { xs: "flex", sm: "flex", md: "none" }, flexDirection: "column", alignItems: "center", mb: 2, gap: 2 }}>
                                {renderSchoolLogos()}
                                {renderHeader()}
                            </Box>
                            <Grid container spacing={1.5} mt={1}>
                                <Grid item xs={12} p={1}>
                                    <Divider><Typography variant="h6">{t("Tell Us About Your Child")}</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="firstName"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    autoFocus
                                                    fullWidth
                                                    label={<RequiredLabel>{t("Child's First Name")}</RequiredLabel>}
                                                    error={!!fieldState.error}
                                                    size="small"
                                                    helperText={fieldState.error?.message}
                                                    {...field}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} >
                                    <Controller
                                        name="middleName"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={t("Child's Middle Name")}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    {...field}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="lastName"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={<RequiredLabel>{t("Child's Last Name")}</RequiredLabel>}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    {...field}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Controller
                                        name="suffix"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <Autocomplete
                                                options={suffixOptions}
                                                getOptionLabel={(option) => option.label}
                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                value={suffixOptions.find(opt => opt.value === field.value) || null}
                                                onChange={(_, newValue) => field.onChange(newValue ? newValue.value : "")}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={t("Child's Suffix")}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                        size="small"
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Controller
                                        name="address"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <>
                                                    <GoogleSearchBox
                                                        label={<RequiredLabel>{t("Address")}</RequiredLabel>}
                                                        error={!!fieldState.error}
                                                        onPlaceSelected={(place) => {
                                                            const getComponent = (type) =>
                                                                place.address_components.find((c) => c.types.includes(type))?.short_name || "";
                                                            const streetNumber = getComponent("street_number");
                                                            const route = getComponent("route");
                                                            const street = [streetNumber, route].filter(Boolean).join(" ");
                                                            const city = getComponent("locality") || getComponent("sublocality") || "";
                                                            const state = getComponent("administrative_area_level_1");
                                                            const zip = getComponent("postal_code");

                                                            const formatedAddress = {
                                                                street,
                                                                city,
                                                                state,
                                                                zip
                                                            }

                                                            field.onChange(formatedAddress);
                                                            setValue("address", formatedAddress);
                                                            setValue("addressState", state || "");
                                                        }}
                                                    />
                                                    {fieldState.error && (
                                                        <FormHelperText sx={{ color: "error.main", marginLeft: 1 }}>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </>
                                            )
                                        }}
                                    />

                                    <Controller
                                        name="addressState"
                                        control={control}
                                        render={({ field }) => (
                                            <input type="hidden" {...field} />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Controller
                                        name="line2"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={t("Address Line 2 (Apt, Suite, etc.)")}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    {...field}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="grade"
                                        control={control}
                                        render={({ field, fieldState }) => {

                                            return (
                                                <Autocomplete
                                                    options={gradeOptions}
                                                    getOptionLabel={(option) => option.label}
                                                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                    value={gradeOptions.find(opt => opt.value === field.value) || null}
                                                    onChange={(_, newValue) => field.onChange(newValue ? newValue.value : "")}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={<RequiredLabel>{t("Grade Entering in")} {"2027-2028"}</RequiredLabel>}
                                                            error={!!fieldState.error}
                                                            helperText={fieldState.error?.message}
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <Controller
                                        name="districtApplying"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            const filteredDistricts = selectedState
                                                ? districtOptions.filter(d =>
                                                    d.states.includes(selectedState) && !d.options.closed &&
                                                    (selectedGrade ? d.gradeLevels.includes(selectedGrade) : true)
                                                )
                                                : districtOptions.filter(d =>
                                                    !d.options.closed &&
                                                    (selectedGrade ? d.gradeLevels.includes(selectedGrade) : true)
                                                );

                                            const options = filteredDistricts.length > 0
                                                ? [
                                                    { value: "__all__", name: t("Select All"), isSelectAll: true },
                                                    ...filteredDistricts.map(d => ({ ...d, isSelectAll: false })),
                                                ]
                                                : [];

                                            const handleChange = (_, newValue) => {
                                                if (newValue.some(opt => opt.value === "__all__")) {
                                                    if (field.value.length === filteredDistricts.length) {
                                                        field.onChange([]);
                                                    } else {
                                                        field.onChange(filteredDistricts.map(opt => opt.id));
                                                    }
                                                } else {
                                                    field.onChange(newValue ? newValue.map(opt => opt.id) : []);
                                                }
                                            };

                                            const selectedValues = filteredDistricts.filter(opt =>
                                                Array.isArray(field.value) ? field.value.includes(opt.id) : false
                                            );

                                            const partiallySelected = field.value.length > 0 && field.value.length < filteredDistricts.length;

                                            return (
                                                <Box>
                                                    <Autocomplete
                                                        multiple
                                                        disableCloseOnSelect
                                                        options={options}
                                                        getOptionLabel={option => option.name}
                                                        noOptionsText={t("No Schools Available for Selected Grade OR Application Period Not Open")}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        value={selectedValues}
                                                        onChange={handleChange}
                                                        renderOption={(props, option) => (
                                                            <li {...props} style={{ paddingLeft: option.isSelectAll ? 8 : 24 }}>
                                                                <Checkbox
                                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                                    indeterminateIcon={<IndeterminateCheckBox fontSize="small" />}
                                                                    indeterminate={option.isSelectAll ? partiallySelected : false}
                                                                    checked={
                                                                        option.isSelectAll
                                                                            ? field.value.length === filteredDistricts.length
                                                                            : field.value.includes(option.id)
                                                                    } style={{ marginRight: 8 }}
                                                                />
                                                                {option.name}
                                                            </li>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={<RequiredLabel>{t("Which school(s) are you applying for?")}</RequiredLabel>}
                                                                error={!!fieldState.error}
                                                                helperText={
                                                                    fieldState.error?.message || t("You may choose as many schools as you wish.")
                                                                }
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        )}
                                                        disabled={(selectedState !== "NJ" && selectedState !== "NY") || selectedGrade === ""}
                                                        ChipProps={{ size: "small" }}
                                                    />
                                                </Box>
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Controller
                                        name="dateOfBirth"
                                        control={control}
                                        render={({ field: { onChange, value, ...rest }, fieldState }) => (
                                            <DatePicker
                                                sx={{ width: "100%" }}
                                                label={<RequiredLabel>{t("Child's Date of Birth")}</RequiredLabel>}
                                                maxDate={dayjs(minDob)}
                                                minDate={dayjs(maxDob)}
                                                disableFuture
                                                value={value ? dayjs(value) : null}
                                                onChange={(newValue) => {
                                                    const date = newValue ? newValue.toDate() : null;
                                                    onChange(date);

                                                    if (date) {
                                                        const ineligible = getIneligibleDistricts(date, selectedDistricts, districtOptions, selectedGrade, currentYear);

                                                        if (ineligible.length > 0 && (selectedGrade === "K" || selectedGrade === "Pre-K")) {
                                                            const msg = ineligible
                                                                .map(d => `${d.name} (cutoff: ${d.options.cutoff.month}/${d.options.cutoff.day})`)
                                                                .join("\n");

                                                            setCutoffMessage(
                                                                `${msg}`
                                                            );
                                                            setCutoffDialogOpen(true);
                                                        }
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        error: !!fieldState.error,
                                                        helperText: fieldState.error?.message,
                                                        size: "small",
                                                        inputProps: { ...rest.inputProps, readOnly: true }
                                                    }
                                                }}
                                                {...rest}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <Controller
                                        name="currentSchool"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label={<RequiredLabel>{t("Current School (If this is your child’s first year in school, write “First Year.”)")}</RequiredLabel>}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                                size="small"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Controller
                                        name="hasSibling"
                                        control={control}
                                        required
                                        render={({ field, fieldState }) => (

                                            <FormControl error={!!fieldState.error} component="fieldset">
                                                <FormLabel><Typography variant="body1" color="text.primary"><RequiredLabel>{t(siblingQuestion)}</RequiredLabel></Typography></FormLabel>
                                                <RadioGroup row {...field}>
                                                    <FormControlLabel value="yes" control={<Radio />} label={t("Yes")} />
                                                    <FormControlLabel value="no" control={<Radio />} label={t("No")} />
                                                </RadioGroup>
                                                {fieldState.error && (
                                                    <FormHelperText color="error">{fieldState.error.message}</FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>


                                {hasSibling === "yes" && (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <Controller
                                                name="siblingFirstName"
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label={<RequiredLabel>{t("Sibling First Name")}</RequiredLabel>}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Controller
                                                name="siblingLastName"
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                    <TextField
                                                        {...field}
                                                        fullWidth
                                                        label={<RequiredLabel>{t("Sibling Last Name")}</RequiredLabel>}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                        size="small"
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Controller
                                                name="siblingGrade"
                                                control={control}
                                                render={({ field, fieldState }) => (
                                                    <Autocomplete
                                                        options={gradeOptions}
                                                        getOptionLabel={(option) => option.label}
                                                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                        value={gradeOptions.find(opt => opt.value === field.value) || null}
                                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.value : "")}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={<RequiredLabel>{t("Sibling Grade")}</RequiredLabel>}
                                                                error={!!fieldState.error}
                                                                helperText={fieldState.error?.message}
                                                                fullWidth
                                                                size="small"
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12} my={2}>
                                    <Divider><Typography variant="h6">{t("Parent/Guardian Contact Information")}</Typography></Divider>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="parentFirstName"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    fullWidth
                                                    label={<RequiredLabel>{t("Parent/Guardian First Name")}</RequiredLabel>}
                                                    error={!!fieldState.error}
                                                    size="small"
                                                    helperText={fieldState.error?.message}
                                                    {...field}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="parentLastName"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    fullWidth
                                                    label={<RequiredLabel>{t("Parent/Guardian Last Name")}</RequiredLabel>}
                                                    error={!!fieldState.error}
                                                    size="small"
                                                    helperText={fieldState.error?.message}
                                                    {...field}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <Controller
                                        name="phoneType"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <Autocomplete
                                                options={phoneTypes}
                                                getOptionLabel={(option) => option.label}
                                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                value={phoneTypes.find(opt => opt.value === field.value) || null}
                                                onChange={(_, newValue) => field.onChange(newValue ? newValue.value : "")}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={<RequiredLabel>{t("Phone Type")}</RequiredLabel>}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                        size="small"
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <PhoneField
                                        name="phoneNumber"
                                        control={control}
                                        label={t("Phone Number")}
                                        required
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label={<RequiredLabel>{t("Email")}</RequiredLabel>}
                                                    error={!!fieldState.error}
                                                    size="small"
                                                    helperText={fieldState.error?.message}
                                                />
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Controller
                                        name="hasEmployedParent"
                                        control={control}
                                        required
                                        render={({ field, fieldState }) => (

                                            <FormControl error={!!fieldState.error} component="fieldset">
                                                <FormLabel><Typography variant="body1" color="text.primary"><RequiredLabel>{t('Is a parent or guardian a certified staff member at any iLearn school?')}</RequiredLabel></Typography></FormLabel>
                                                <RadioGroup row {...field}>
                                                    <FormControlLabel value="yes" control={<Radio />} label={t("Yes")} />
                                                    <FormControlLabel value="no" control={<Radio />} label={t("No")} />
                                                </RadioGroup>
                                                {fieldState.error && (
                                                    <FormHelperText color="error">{fieldState.error.message}</FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                {hasEmployedParent === "yes" && (
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="staffEmail"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    size="small"
                                                    label={<RequiredLabel>{t("Work Email of the Employed Parent/Guardian")}</RequiredLabel>}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message || t("We use this to confirm employment.")}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                                {showWeightedLottery &&
                                    <Grid item xs={12} my={2}>
                                        <Divider><Typography variant="h6">{t("Weighted Lottery")}</Typography></Divider>
                                    </Grid>
                                }

                                {hasELL && (
                                    <Grid item xs={12}>
                                        <Controller
                                            name="isELL"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <FormControl error={!!fieldState.error} component="fieldset">
                                                    <FormLabel><Typography variant="body1" color="text.primary"><RequiredLabel>{t("Is your child an English Language Learner (ELL)?")}</RequiredLabel></Typography></FormLabel>
                                                    <RadioGroup row {...field}>
                                                        <FormControlLabel value="yes" control={<Radio />} label={t("Yes")} />
                                                        <FormControlLabel value="no" control={<Radio />} label={t("No")} />
                                                    </RadioGroup>
                                                    {fieldState.error && (
                                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}

                                {hasLowIncome && (
                                    <Grid item xs={12}>
                                        <Controller
                                            name="isLowIncome"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <FormControl error={!!fieldState.error} component="fieldset">
                                                    <FormLabel><Typography variant="body1" color="text.primary"><RequiredLabel>{t("Does your child qualify for economically disadvantaged category?")}</RequiredLabel></Typography></FormLabel>
                                                    <RadioGroup row {...field}>
                                                        <FormControlLabel value="yes" control={<Radio />} label={t("Yes")} />
                                                        <FormControlLabel value="no" control={<Radio />} label={t("No")} />
                                                    </RadioGroup>
                                                    {fieldState.error && (
                                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}

                                {hasSwd && (
                                    <Grid item xs={12}>
                                        <Controller
                                            name="isSWD"
                                            control={control}
                                            render={({ field, fieldState }) => (
                                                <FormControl error={!!fieldState.error} component="fieldset">
                                                    <FormLabel><Typography variant="body1" color="text.primary"><RequiredLabel>{t("Does your child qualify for the Special Education category?")}</RequiredLabel></Typography></FormLabel>
                                                    <RadioGroup row {...field}>
                                                        <FormControlLabel value="yes" control={<Radio />} label={t("Yes")} />
                                                        <FormControlLabel value="no" control={<Radio />} label={t("No")} />
                                                    </RadioGroup>
                                                    {fieldState.error && (
                                                        <FormHelperText>{fieldState.error.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} my={2}>
                                    <Divider><Typography variant="h6">{t("Confirmation")}</Typography></Divider>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="body1" fontWeight={'bold'}><RequiredLabel>{t('Certification')}</RequiredLabel></Typography>
                                    <Controller
                                        name="certification"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <FormControl error={!!fieldState.error} component="fieldset">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={field.value || false}
                                                            onChange={e => field.onChange(e.target.checked)}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body1">
                                                                {t("I confirm that all the information I entered is true and complete.")}
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {t("I understand that false or duplicate information may cause this application to be rejected.")}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                                {fieldState.error && (
                                                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <Button variant="contained" color="primary" type="submit" data-label="submit-application-button">
                                    {t('Submit Application')}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            ) : (
                <ThankYou onBack={() => setSubmitted(false)} submissionData={submissionData} />
            )}
            <Paper sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }}>
                <ToggleButtonGroup value={i18n.resolvedLanguage} exclusive onChange={handleLangChange} size="small" color="primary">
                    {Object.keys(languages).map(lang => (
                        <ToggleButton value={lang} key={lang}>
                            {lang}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Paper>
            <SiblingDialog open={openSiblingInfo} onClose={() => setOpenSiblingInfo(false)} />
            <ELLDialog open={openELLInfo} onClose={() => setOpenELLInfo(false)} reset={resetField} />
            <FinancialDialog open={openFinancialInfo} onClose={() => setOpenFinancialInfo(false)} reset={resetField} />
            <CutoffDialog open={cutoffDialogOpen} message={cutoffMessage} onClose={() => setCutoffDialogOpen(false)} />
            <SpecialEdDialog open={openSpecialEdInfo} onClose={() => setOpenSpecialEdInfo(false)} reset={resetField} />
            <StaffPriorityDialog open={openStaffInfo} onClose={() => setOpenStaffInfo(false)} />
        </>
    );
}
